import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Calendar,
  DollarSign,
  Settings,
  Shield,
  Search,
  Check,
  X,
  Eye,
  Ban,
  RefreshCw,
  ToggleLeft,
  Loader2,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// ==================== STATUS BADGE ====================
const StatusBadge = ({ status }) => {
  const variants = {
    pending: { className: 'bg-soft-amber/20 text-soft-amber border-soft-amber/30', label: 'Pending' },
    active: { className: 'bg-fresh-mint/20 text-fresh-mint border-fresh-mint/30', label: 'Active' },
    suspended: { className: 'bg-signal-red/20 text-signal-red border-signal-red/30', label: 'Suspended' },
  };
  
  const variant = variants[status] || variants.pending;
  
  return (
    <Badge className={`${variant.className} border`} data-testid={`status-badge-${status}`}>
      {variant.label}
    </Badge>
  );
};

// ==================== ORGANIZATIONS TAB ====================
const OrganizationsTab = ({ session, onViewOrg }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [suspendModal, setSuspendModal] = useState({ open: false, org: null });
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  
  const fetchOrganizations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const res = await fetch(`${API_URL}/api/platform-admin/organizations?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      console.error('Fetch orgs error:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [session.access_token, search, statusFilter]);
  
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);
  
  const handleApprove = async (orgId) => {
    setActionLoading(orgId);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/organizations/${orgId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        toast.success('Organization approved');
        fetchOrganizations();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve organization');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleSuspend = async () => {
    if (!suspendModal.org || !suspendReason.trim()) return;
    
    setActionLoading(suspendModal.org.id);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/organizations/${suspendModal.org.id}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ reason: suspendReason }),
      });
      
      if (res.ok) {
        toast.success('Organization suspended');
        setSuspendModal({ open: false, org: null });
        setSuspendReason('');
        fetchOrganizations();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to suspend');
      }
    } catch (error) {
      toast.error('Failed to suspend organization');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleReactivate = async (orgId) => {
    setActionLoading(orgId);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/organizations/${orgId}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        toast.success('Organization reactivated');
        fetchOrganizations();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reactivate');
      }
    } catch (error) {
      toast.error('Failed to reactivate organization');
    } finally {
      setActionLoading(null);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="org-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-obsidian border border-white/[0.08] rounded-lg text-cloud-white"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <Button
          onClick={fetchOrganizations}
          variant="outline"
          className="border-white/[0.08]"
          data-testid="refresh-orgs-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Table */}
      <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="px-4 py-3 text-left text-sm font-medium text-soft-silver">Organization</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-soft-silver">Campus</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-soft-silver">Status</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-soft-silver">Members</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-soft-silver">Events</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-soft-silver">Plan</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-soft-silver">Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                <td className="px-4 py-4">
                  <div>
                    <p className="text-cloud-white font-medium">{org.name}</p>
                    <p className="text-xs text-muted-text">{org.type}</p>
                  </div>
                </td>
                <td className="px-4 py-4 text-soft-silver">{org.campus_name || '-'}</td>
                <td className="px-4 py-4 text-center">
                  <StatusBadge status={org.status} />
                </td>
                <td className="px-4 py-4 text-center text-soft-silver">{org.member_count}</td>
                <td className="px-4 py-4 text-center text-soft-silver">{org.event_count}</td>
                <td className="px-4 py-4 text-center">
                  <Badge variant="outline" className="border-electric-ice-blue/30 text-electric-ice-blue">
                    {org.plan_name}
                  </Badge>
                </td>
                <td className="px-4 py-4">
                  <div className="flex justify-end gap-2">
                    {org.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleApprove(org.id)}
                        disabled={actionLoading === org.id}
                        className="bg-fresh-mint/20 text-fresh-mint hover:bg-fresh-mint/30"
                        data-testid={`approve-btn-${org.id}`}
                      >
                        {actionLoading === org.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    {org.status === 'active' && (
                      <Button
                        size="sm"
                        onClick={() => setSuspendModal({ open: true, org })}
                        disabled={actionLoading === org.id}
                        className="bg-signal-red/20 text-signal-red hover:bg-signal-red/30"
                        data-testid={`suspend-btn-${org.id}`}
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                    {org.status === 'suspended' && (
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(org.id)}
                        disabled={actionLoading === org.id}
                        className="bg-fresh-mint/20 text-fresh-mint hover:bg-fresh-mint/30"
                        data-testid={`reactivate-btn-${org.id}`}
                      >
                        {actionLoading === org.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewOrg(org)}
                      className="border-white/[0.08]"
                      data-testid={`view-btn-${org.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-muted-text">
                  No organizations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Suspend Modal */}
      <Dialog open={suspendModal.open} onOpenChange={(open) => !open && setSuspendModal({ open: false, org: null })}>
        <DialogContent className="bg-deep-space border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-cloud-white">Suspend Organization</DialogTitle>
            <DialogDescription className="text-muted-text">
              This will prevent all members of {suspendModal.org?.name} from accessing the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-soft-silver mb-2 block">Suspension Reason *</label>
            <Input
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension..."
              className="bg-obsidian border-white/[0.08] text-cloud-white"
              data-testid="suspend-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuspendModal({ open: false, org: null })}
              className="border-white/[0.08]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSuspend}
              disabled={!suspendReason.trim() || actionLoading}
              className="bg-signal-red text-white hover:bg-signal-red/90"
              data-testid="confirm-suspend-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Suspend Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== FEATURE FLAGS TAB ====================
const FeatureFlagsTab = ({ session }) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform-admin/organizations?page_size=100`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch (error) {
        console.error('Fetch orgs error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrgs();
  }, [session.access_token]);
  
  const fetchFlags = async (orgId) => {
    setFlagsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/feature-flags/${orgId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFlags(data.flags || []);
        setSelectedOrg({ id: orgId, name: data.organization_name });
      }
    } catch (error) {
      console.error('Fetch flags error:', error);
      toast.error('Failed to load feature flags');
    } finally {
      setFlagsLoading(false);
    }
  };
  
  const toggleFlag = async (featureName, enabled) => {
    if (!selectedOrg) return;
    setUpdating(featureName);
    
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/feature-flags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          organization_id: selectedOrg.id,
          feature_name: featureName,
          enabled: !enabled,
        }),
      });
      
      if (res.ok) {
        setFlags((prev) =>
          prev.map((f) => (f.feature_name === featureName ? { ...f, enabled: !enabled } : f))
        );
        toast.success(`${featureName} ${!enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to update feature flag');
    } finally {
      setUpdating(null);
    }
  };
  
  const featureLabels = {
    age_verification: 'Age Verification',
    document_vault: 'Document Vault',
    analytics: 'Analytics Dashboard',
    rush_pipeline: 'Rush Pipeline',
    sponsor_referrals: 'Sponsor Referrals',
    dues_management: 'Dues Management',
    recruitment: 'Recruitment',
    door_staff: 'Door Staff',
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Organizations List */}
      <div className="col-span-1 bg-obsidian/50 border border-white/[0.08] rounded-xl p-4 max-h-[600px] overflow-y-auto">
        <h3 className="text-sm font-medium text-soft-silver mb-4">Organizations</h3>
        <div className="space-y-1">
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => fetchFlags(org.id)}
              className={`w-full px-3 py-2 text-left rounded-lg transition-colors ${
                selectedOrg?.id === org.id
                  ? 'bg-electric-ice-blue/20 text-electric-ice-blue'
                  : 'text-soft-silver hover:bg-white/[0.04]'
              }`}
              data-testid={`org-select-${org.id}`}
            >
              <p className="font-medium truncate">{org.name}</p>
              <p className="text-xs text-muted-text">{org.type}</p>
            </button>
          ))}
        </div>
      </div>
      
      {/* Feature Flags */}
      <div className="col-span-2 bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
        {!selectedOrg ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-text">
            <ToggleLeft className="w-12 h-12 mb-4 opacity-50" />
            <p>Select an organization to manage feature flags</p>
          </div>
        ) : flagsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-cloud-white mb-6">
              Feature Flags for {selectedOrg.name}
            </h3>
            <div className="space-y-4">
              {flags.map((flag) => (
                <div
                  key={flag.feature_name}
                  className="flex items-center justify-between p-4 bg-deep-space rounded-lg border border-white/[0.04]"
                >
                  <div>
                    <p className="text-cloud-white font-medium">
                      {featureLabels[flag.feature_name] || flag.feature_name}
                    </p>
                    <p className="text-xs text-muted-text">{flag.feature_name}</p>
                  </div>
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={() => toggleFlag(flag.feature_name, flag.enabled)}
                    disabled={updating === flag.feature_name}
                    data-testid={`toggle-${flag.feature_name}`}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== REVENUE TAB ====================
const RevenueTab = ({ session }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform-admin/revenue`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRevenueData(data);
        }
      } catch (error) {
        console.error('Fetch revenue error:', error);
        toast.error('Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };
    fetchRevenue();
  }, [session.access_token]);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }
  
  if (!revenueData) {
    return (
      <div className="text-center py-12 text-muted-text">
        Failed to load revenue data
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Platform Totals */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
          <p className="text-sm text-muted-text mb-2">Total Gross Revenue</p>
          <p className="text-2xl font-bold text-fresh-mint" data-testid="total-gross">
            {formatCurrency(revenueData.platform_totals.total_gross_revenue)}
          </p>
        </div>
        <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
          <p className="text-sm text-muted-text mb-2">Total Payouts</p>
          <p className="text-2xl font-bold text-soft-amber" data-testid="total-payouts">
            {formatCurrency(revenueData.platform_totals.total_payouts)}
          </p>
        </div>
        <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
          <p className="text-sm text-muted-text mb-2">Net Balance</p>
          <p className="text-2xl font-bold text-electric-ice-blue" data-testid="net-balance">
            {formatCurrency(revenueData.platform_totals.net_balance)}
          </p>
        </div>
      </div>
      
      {/* Organization Revenue Table */}
      <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="px-4 py-3 text-left text-sm font-medium text-soft-silver">Organization</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-soft-silver">Gross Revenue</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-soft-silver">Payouts</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-soft-silver">Net Balance</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.organizations.map((org) => (
              <tr key={org.organization_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                <td className="px-4 py-4 text-cloud-white">{org.organization_name}</td>
                <td className="px-4 py-4 text-right text-fresh-mint font-mono">
                  {formatCurrency(org.gross_revenue)}
                </td>
                <td className="px-4 py-4 text-right text-soft-amber font-mono">
                  {formatCurrency(org.total_payouts)}
                </td>
                <td className="px-4 py-4 text-right text-electric-ice-blue font-mono">
                  {formatCurrency(org.net_balance)}
                </td>
              </tr>
            ))}
            {revenueData.organizations.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-text">
                  No revenue data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== PLATFORM CONFIG TAB ====================
const PlatformConfigTab = ({ session }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  
  // Form state
  const [publicKey, setPublicKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [sandboxMode, setSandboxMode] = useState(true);
  const [defaultMinAge, setDefaultMinAge] = useState(21);
  const [perVerificationFee, setPerVerificationFee] = useState(0);
  
  // Claude AI state
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [claudeModel, setClaudeModel] = useState('claude-sonnet-4-20250514');
  const [aiQuotaFree, setAiQuotaFree] = useState(50);
  const [aiQuotaPro, setAiQuotaPro] = useState(500);
  
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform-admin/config`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setConfig(data);
          setSandboxMode(data.age_verification.sandbox_mode);
          setDefaultMinAge(data.age_verification.default_min_age);
          setPerVerificationFee(data.age_verification.per_verification_fee);
          // Load Claude AI config
          if (data.ai_assistant) {
            setClaudeModel(data.ai_assistant.model || 'claude-sonnet-4-20250514');
            setAiQuotaFree(data.ai_assistant.quota_free || 50);
            setAiQuotaPro(data.ai_assistant.quota_pro || 500);
          }
        }
      } catch (error) {
        console.error('Fetch config error:', error);
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [session.access_token]);
  
  const saveApiKeys = async () => {
    if (!publicKey && !secretKey) {
      toast.error('Enter at least one key to update');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/config/age-verification-keys`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          public_key: publicKey || undefined,
          secret_key: secretKey || undefined,
        }),
      });
      
      if (res.ok) {
        toast.success('API keys updated');
        setPublicKey('');
        setSecretKey('');
        // Refresh config
        const configRes = await fetch(`${API_URL}/api/platform-admin/config`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (configRes.ok) {
          setConfig(await configRes.json());
        }
      }
    } catch (error) {
      toast.error('Failed to save API keys');
    } finally {
      setSaving(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          sandbox_mode: sandboxMode,
          default_min_age: defaultMinAge,
          per_verification_fee: perVerificationFee,
        }),
      });
      
      if (res.ok) {
        toast.success('Settings saved');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/config/test-age-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };
  
  const saveClaudeApiKey = async () => {
    if (!claudeApiKey) {
      toast.error('Enter Claude API key');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/config/claude-api-key`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          api_key: claudeApiKey,
          model: claudeModel,
          quota_free: aiQuotaFree,
          quota_pro: aiQuotaPro,
        }),
      });
      
      if (res.ok) {
        toast.success('Claude AI settings updated');
        setClaudeApiKey('');
        // Refresh config
        const configRes = await fetch(`${API_URL}/api/platform-admin/config`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (configRes.ok) {
          setConfig(await configRes.json());
        }
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to save Claude settings');
      }
    } catch (error) {
      toast.error('Failed to save Claude settings');
    } finally {
      setSaving(false);
    }
  };
  
  const testClaudeAI = async () => {
    setTestingAI(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/config/test-claude`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Claude AI connection successful!');
      } else {
        toast.error(data.error || 'Claude AI test failed');
      }
    } catch (error) {
      toast.error('Claude AI test failed');
    } finally {
      setTestingAI(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Age Verification Section */}
      <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-cloud-white mb-6 flex items-center gap-2">
          <Shield className="w-5 h-5 text-electric-ice-blue" />
          Age Verification (PassportReader)
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-soft-silver mb-2 block">Public Key</label>
            <div className="flex gap-2">
              <Input
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder={config?.age_verification.has_public_key ? config.age_verification.public_key_masked : 'Enter public key...'}
                className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
                data-testid="public-key-input"
              />
            </div>
            {config?.age_verification.has_public_key && (
              <p className="text-xs text-muted-text mt-1">
                Current: {config.age_verification.public_key_masked}
              </p>
            )}
          </div>
          
          <div>
            <label className="text-sm text-soft-silver mb-2 block">Secret Key</label>
            <Input
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder="Enter secret key..."
              className="bg-deep-space border-white/[0.08] text-cloud-white"
              data-testid="secret-key-input"
            />
            <p className="text-xs text-muted-text mt-1">Never shown after saving</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={saveApiKeys}
              disabled={saving || (!publicKey && !secretKey)}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="save-keys-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Keys
            </Button>
            <Button
              onClick={testConnection}
              disabled={testingConnection || !config?.age_verification.has_public_key}
              variant="outline"
              className="border-white/[0.08]"
              data-testid="test-connection-btn"
            >
              {testingConnection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Test Connection
            </Button>
          </div>
          
          <div className="pt-4 border-t border-white/[0.08]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cloud-white">Sandbox Mode</p>
                <p className="text-xs text-muted-text">Use test environment for verification</p>
              </div>
              <Switch
                checked={sandboxMode}
                onCheckedChange={setSandboxMode}
                data-testid="sandbox-mode-toggle"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Platform Settings Section */}
      <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-cloud-white mb-6 flex items-center gap-2">
          <Settings className="w-5 h-5 text-soft-amber" />
          Platform Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-soft-silver mb-2 block">Default Minimum Age</label>
            <Input
              type="number"
              min={18}
              max={99}
              value={defaultMinAge}
              onChange={(e) => setDefaultMinAge(parseInt(e.target.value) || 21)}
              className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
              data-testid="default-min-age-input"
            />
          </div>
          
          <div>
            <label className="text-sm text-soft-silver mb-2 block">Per Verification Fee ($)</label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={perVerificationFee}
              onChange={(e) => setPerVerificationFee(parseFloat(e.target.value) || 0)}
              className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
              data-testid="verification-fee-input"
            />
          </div>
          
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-soft-amber text-obsidian hover:bg-soft-amber/90"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
        </div>
      </div>
      
      {/* Claude AI Configuration Section */}
      <div className="col-span-2 bg-obsidian/50 border border-white/[0.08] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-cloud-white mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#B794F4]" />
          Claude AI Assistant
          {config?.ai_assistant?.has_api_key && (
            <span className="ml-2 text-xs bg-electric-green/20 text-electric-green px-2 py-0.5 rounded-full">
              Configured
            </span>
          )}
        </h3>
        
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-soft-silver mb-2 block">Claude API Key</label>
              <Input
                type="password"
                value={claudeApiKey}
                onChange={(e) => setClaudeApiKey(e.target.value)}
                placeholder={config?.ai_assistant?.has_api_key ? '••••••••••••••••' : 'sk-ant-...'}
                className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
                data-testid="claude-api-key-input"
              />
              <p className="text-xs text-muted-text mt-1">
                {config?.ai_assistant?.has_api_key 
                  ? 'Key is configured. Enter new key to update.' 
                  : 'Get your API key from console.anthropic.com'}
              </p>
            </div>
            
            <div>
              <label className="text-sm text-soft-silver mb-2 block">Model</label>
              <select
                value={claudeModel}
                onChange={(e) => setClaudeModel(e.target.value)}
                className="w-full h-10 rounded-md bg-deep-space border border-white/[0.08] text-cloud-white px-3"
                data-testid="claude-model-select"
              >
                <option value="claude-sonnet-4-20250514">Claude Sonnet 4 (Recommended)</option>
                <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                <option value="claude-3-haiku-20240307">Claude 3 Haiku (Faster)</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={saveClaudeApiKey}
                disabled={saving || !claudeApiKey}
                className="bg-[#B794F4] text-white hover:bg-[#9F7AEA]"
                data-testid="save-claude-key-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save API Key
              </Button>
              <Button
                onClick={testClaudeAI}
                disabled={testingAI || !config?.ai_assistant?.has_api_key}
                variant="outline"
                className="border-white/[0.08]"
                data-testid="test-claude-btn"
              >
                {testingAI ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Test Connection
              </Button>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-soft-silver mb-2 block">Monthly Quota (Free Plan)</label>
              <Input
                type="number"
                min={0}
                value={aiQuotaFree}
                onChange={(e) => setAiQuotaFree(parseInt(e.target.value) || 50)}
                className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
                data-testid="ai-quota-free-input"
              />
              <p className="text-xs text-muted-text mt-1">AI calls per month for free organizations</p>
            </div>
            
            <div>
              <label className="text-sm text-soft-silver mb-2 block">Monthly Quota (Pro Plan)</label>
              <Input
                type="number"
                min={0}
                value={aiQuotaPro}
                onChange={(e) => setAiQuotaPro(parseInt(e.target.value) || 500)}
                className="bg-deep-space border-white/[0.08] text-cloud-white font-mono"
                data-testid="ai-quota-pro-input"
              />
              <p className="text-xs text-muted-text mt-1">AI calls per month for pro organizations</p>
            </div>
            
            {config?.ai_assistant && (
              <div className="p-3 bg-deep-space rounded-lg">
                <p className="text-sm text-soft-silver mb-2">Platform AI Stats</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-text">Total calls this month:</span>
                  <span className="text-cloud-white font-mono">{config.ai_assistant.total_calls_this_month || 0}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN ADMIN DASHBOARD ====================
export const PlatformAdmin = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasAccess, setHasAccess] = useState(null);
  const [platformRole, setPlatformRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('organizations');
  const [viewingOrg, setViewingOrg] = useState(null);
  
  // Check platform admin access
  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    
    const checkAccess = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform-admin/check-access`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        
        if (res.ok) {
          const data = await res.json();
          setHasAccess(data.has_access);
          setPlatformRole(data.role);
          
          if (!data.has_access) {
            navigate('/app');
          }
        } else {
          setHasAccess(false);
          navigate('/app');
        }
      } catch (error) {
        console.error('Access check error:', error);
        setHasAccess(false);
        navigate('/app');
      } finally {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [session?.access_token, navigate]);
  
  // Handle approve from email link
  useEffect(() => {
    const approveOrgId = searchParams.get('approve');
    if (approveOrgId && hasAccess) {
      // Auto-approve the org
      const autoApprove = async () => {
        try {
          const res = await fetch(`${API_URL}/api/platform-admin/organizations/${approveOrgId}/approve`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          
          if (res.ok) {
            toast.success('Organization approved!');
          }
        } catch (error) {
          console.error('Auto-approve error:', error);
        }
        
        // Clear the param
        setSearchParams({});
      };
      
      autoApprove();
    }
  }, [hasAccess, searchParams, session?.access_token, setSearchParams]);
  
  const handleViewOrg = (org) => {
    setViewingOrg(org);
    // Navigate to view org as admin
    window.open(`/admin/orgs/${org.id}`, '_blank');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }
  
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-signal-red mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-cloud-white mb-2">Access Denied</h1>
          <p className="text-muted-text mb-6">You don't have permission to access this page.</p>
          <Button onClick={() => navigate('/app')}>Return to Dashboard</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-space">
      {/* Header */}
      <header className="border-b border-white/[0.08] bg-obsidian/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/assets/app-icon.png" 
              alt="CamPassed" 
              className="w-10 h-10 rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-cloud-white">Platform Admin</h1>
              <p className="text-xs text-electric-ice-blue">{platformRole?.replace('_', ' ')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/app')}
            variant="outline"
            className="border-white/[0.08]"
            data-testid="exit-admin-btn"
          >
            Exit Admin
          </Button>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-obsidian/50 border border-white/[0.08] mb-8">
            <TabsTrigger value="organizations" className="data-[state=active]:bg-electric-ice-blue/20" data-testid="tab-organizations">
              <Building2 className="w-4 h-4 mr-2" />
              Organizations
            </TabsTrigger>
            <TabsTrigger value="feature-flags" className="data-[state=active]:bg-electric-ice-blue/20" data-testid="tab-feature-flags">
              <ToggleLeft className="w-4 h-4 mr-2" />
              Feature Flags
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-electric-ice-blue/20" data-testid="tab-revenue">
              <DollarSign className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-electric-ice-blue/20" data-testid="tab-config">
              <Settings className="w-4 h-4 mr-2" />
              Platform Config
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="organizations">
            <OrganizationsTab session={session} onViewOrg={handleViewOrg} />
          </TabsContent>
          
          <TabsContent value="feature-flags">
            <FeatureFlagsTab session={session} />
          </TabsContent>
          
          <TabsContent value="revenue">
            <RevenueTab session={session} />
          </TabsContent>
          
          <TabsContent value="config">
            <PlatformConfigTab session={session} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PlatformAdmin;
